import React from 'react';

/**
 * TayLord Arms branded INVOICE layout. Visually mirrors the structured estimate
 * (Navy/Orange paper template) but with invoice-specific semantics:
 *   - Title "INVOICE" + meta box showing INVOICE #, DATE, DUE DATE
 *   - PAID / PARTIAL / UNPAID diagonal status stamp
 *   - Totals box highlights TOTAL DUE and BALANCE DUE
 *   - Payment history section (auto-fed by recorded payments)
 */

const NAVY = '#15315c';
const ORANGE = '#f57c00';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    if (typeof iso === 'string' && iso.length === 10) {
      return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const STAMP = {
  paid: { label: 'PAID', color: '#15803d' },
  partial: { label: 'PARTIAL', color: '#b45309' },
  unpaid: { label: 'UNPAID', color: '#b91c1c' },
};

const FieldLine = ({ label, value, testid, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <div className="flex items-baseline gap-2">
      <span className="uppercase font-bold text-[10px] tracking-wider shrink-0" style={{ color: NAVY }}>{label}</span>
      <div className="flex-1 border-b border-slate-700 min-h-[18px] text-sm text-slate-900 font-medium px-1 break-words" data-testid={testid}>{value || '\u00A0'}</div>
    </div>
  </div>
);

const SectionBar = ({ children }) => (
  <h3 className="font-extrabold text-sm tracking-wider mb-2 pb-1 border-b-2" style={{ color: NAVY, borderColor: NAVY }}>{children}</h3>
);

const ItemsTable = ({ title, rows, columns, testid }) => (
  <div className="border-2 rounded-sm overflow-hidden" style={{ borderColor: NAVY }} data-testid={testid}>
    <div className="text-center font-extrabold text-white text-sm tracking-widest py-1.5" style={{ background: NAVY }}>{title}</div>
    <table className="w-full text-[11px]">
      <thead className="text-center font-bold" style={{ color: NAVY }}>
        <tr className="border-b" style={{ borderColor: NAVY }}>
          {columns.map((c) => <th key={c.k} className="py-1 px-1" style={{ width: c.w }}>{c.h}</th>)}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: Math.max(rows.length, 6) }).map((_, idx) => {
          const r = rows[idx];
          return (
            <tr key={`${testid}-${idx}`} className="border-b border-slate-300">
              {columns.map((c) => (
                <td key={c.k} className="px-1 py-1 align-bottom border-b border-slate-300 tabular-nums" style={{ textAlign: c.align || 'left' }}>
                  <span className="block min-h-[14px]">{r ? (c.fmt ? c.fmt(r) : r[c.k]) : '\u00A0'}</span>
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const StructuredInvoiceView = ({ invoice }) => {
  const items = invoice.items_computed || invoice.line_items || [];
  const labor = items.filter((i) => (i.category || 'parts') === 'labor');
  const parts = items.filter((i) => (i.category || 'parts') !== 'labor');
  const firearm = invoice.firearm || {};
  const status = STAMP[invoice.payment_status] || STAMP.unpaid;
  const balanceDue = Number(invoice.balance_due || 0);
  const totalDue = Number(invoice.total || 0);

  return (
    <div data-testid="invoice-structured-render" className="bg-white text-slate-900 p-6 sm:p-8 max-w-4xl mx-auto shadow-lg print:shadow-none print:p-6 rounded-md print:rounded-none relative overflow-hidden">
      {/* Diagonal status watermark */}
      <div
        aria-hidden="true"
        data-testid="invoice-status-stamp"
        className="pointer-events-none absolute right-10 top-44 rotate-[-18deg] border-4 px-6 py-1 text-4xl font-extrabold tracking-widest opacity-20"
        style={{ color: status.color, borderColor: status.color }}
      >
        {status.label}
      </div>

      {/* ===== Header ===== */}
      <header className="grid grid-cols-12 gap-4 items-start mb-4 pb-4 border-b-2" style={{ borderColor: NAVY }}>
        <div className="col-span-3">
          <img src="/logo-taylordarms-oval.png" alt="TayLord Arms" className="w-full max-w-[170px] object-contain" />
        </div>
        <div className="col-span-6 text-center pt-1">
          <h1 className="font-extrabold text-5xl tracking-tight" style={{ color: NAVY }}>INVOICE</h1>
          <p className="font-bold text-sm tracking-[0.3em] mt-1" style={{ color: ORANGE }}>— PROFESSIONAL FIREARM SERVICES —</p>
          <div className="mt-3 space-y-0.5 text-[12px]" style={{ color: NAVY }}>
            <p>1102 Maplecrest Dr</p>
            <p>Moncks Corner, SC 29461</p>
            <p>www.taylordarms.com</p>
            <p>info.taylordarms@gmail.com</p>
            <p>843.209.4304</p>
          </div>
        </div>
        <div className="col-span-3 border-2 rounded-sm p-3 text-[11px] space-y-2" style={{ borderColor: NAVY, color: NAVY }}>
          <div>
            <span className="font-bold tracking-wider">INVOICE # </span>
            <span className="border-b border-slate-700 inline-block min-w-[80px] font-mono text-sm font-bold" data-testid="render-invoice-number">{invoice.invoice_number || '\u00A0'}</span>
          </div>
          <div>
            <span className="font-bold tracking-wider">DATE </span>
            <span className="border-b border-slate-700 inline-block min-w-[120px]">{fmtDate(invoice.created_at)}</span>
          </div>
          <div>
            <span className="font-bold tracking-wider">DUE DATE </span>
            <span className="border-b border-slate-700 inline-block min-w-[100px]">{fmtDate(invoice.due_date)}</span>
          </div>
          {invoice.estimate_number && (
            <div className="text-[10px] pt-1 border-t border-slate-300">
              <span className="font-bold tracking-wider">FROM EST. </span>
              <span className="font-mono">{invoice.estimate_number}</span>
            </div>
          )}
        </div>
      </header>

      {/* ===== Customer Information ===== */}
      <section className="mb-4">
        <SectionBar>BILL TO</SectionBar>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          <FieldLine label="NAME" value={invoice.customer_name} testid="render-customer-name" />
          <FieldLine label="PHONE" value={invoice.customer_phone} />
          <FieldLine label="EMAIL" value={invoice.customer_email} />
          <FieldLine label="ADDRESS" value={invoice.customer_address} />
        </div>
      </section>

      {/* ===== Firearm Information ===== */}
      {(firearm.make || firearm.model || firearm.caliber || firearm.type || firearm.serial || firearm.color || firearm.accessories) && (
        <section className="mb-4">
          <SectionBar>FIREARM INFORMATION</SectionBar>
          <div className="grid grid-cols-3 gap-x-8 gap-y-2">
            <FieldLine label="MAKE" value={firearm.make} testid="render-firearm-make" />
            <FieldLine label="MODEL" value={firearm.model} />
            <FieldLine label="CALIBER" value={firearm.caliber} />
            <FieldLine label="TYPE" value={firearm.type} />
            <FieldLine label="SERIAL #" value={firearm.serial} />
            <FieldLine label="COLOR / FINISH" value={firearm.color} />
          </div>
          {firearm.accessories && (
            <div className="grid grid-cols-1 mt-2">
              <FieldLine label="OTHER / OPTIC / ACCESSORIES" value={firearm.accessories} />
            </div>
          )}
        </section>
      )}

      {/* ===== Service Description ===== */}
      {invoice.service_description && (
        <section className="mb-4">
          <SectionBar>SERVICE PERFORMED / DESCRIPTION OF WORK</SectionBar>
          <div className="text-sm text-slate-900 whitespace-pre-wrap min-h-[3em] border-b border-slate-700 pb-1" data-testid="render-service-description">
            {invoice.service_description}
          </div>
        </section>
      )}

      {/* ===== Labor + Parts tables ===== */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <ItemsTable
          title="LABOR"
          testid="render-labor-table"
          rows={labor}
          columns={[
            { k: 'label', h: 'DESCRIPTION', w: '70%', align: 'left' },
            { k: 'line_total', h: 'AMOUNT', w: '30%', align: 'right', fmt: (r) => `$ ${(r.line_total || (r.qty * r.unit_price) || 0).toFixed(2)}` },
          ]}
        />
        <ItemsTable
          title="PARTS / MATERIALS"
          testid="render-parts-table"
          rows={parts}
          columns={[
            { k: 'label', h: 'DESCRIPTION', w: '45%', align: 'left' },
            { k: 'qty', h: 'QTY', w: '12%', align: 'center' },
            { k: 'unit_price', h: 'UNIT PRICE', w: '20%', align: 'right', fmt: (r) => `$ ${(r.unit_price || 0).toFixed(2)}` },
            { k: 'line_total', h: 'TOTAL', w: '23%', align: 'right', fmt: (r) => `$ ${(r.line_total || (r.qty * r.unit_price) || 0).toFixed(2)}` },
          ]}
        />
      </div>

      {/* ===== Notes (left) + Totals (right) ===== */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <section>
          {invoice.gunsmith_notes && (
            <div>
              <span className="uppercase font-bold text-[10px] tracking-wider block mb-1" style={{ color: NAVY }}>NOTES</span>
              <div className="text-sm text-slate-900 whitespace-pre-wrap min-h-[3em] border-b border-slate-700">
                {invoice.gunsmith_notes}
              </div>
            </div>
          )}
          {invoice.notes && (
            <div className="mt-3">
              <span className="uppercase font-bold text-[10px] tracking-wider block mb-1" style={{ color: NAVY }}>TERMS &amp; INSTRUCTIONS</span>
              <div className="text-sm text-slate-900 whitespace-pre-wrap min-h-[3em] border-b border-slate-700">
                {invoice.notes}
              </div>
            </div>
          )}
        </section>
        <section className="border-2 rounded-sm overflow-hidden" style={{ borderColor: NAVY }}>
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="font-bold py-1.5 px-3" style={{ color: NAVY }}>LABOR TOTAL</td><td className="text-right py-1.5 px-3 tabular-nums">$ {(invoice.labor_total || 0).toFixed(2)}</td></tr>
              <tr><td className="font-bold py-1.5 px-3 border-t border-slate-300" style={{ color: NAVY }}>PARTS TOTAL</td><td className="text-right py-1.5 px-3 border-t border-slate-300 tabular-nums">$ {(invoice.parts_total || 0).toFixed(2)}</td></tr>
              <tr><td className="font-bold py-1.5 px-3 border-t border-slate-300" style={{ color: NAVY }}>SUBTOTAL</td><td className="text-right py-1.5 px-3 border-t border-slate-300 tabular-nums">$ {(invoice.subtotal || 0).toFixed(2)}</td></tr>
              <tr><td className="font-bold py-1.5 px-3 border-t border-slate-300" style={{ color: NAVY }}>TAX ({invoice.tax_rate || 0}%)</td><td className="text-right py-1.5 px-3 border-t border-slate-300 tabular-nums">$ {(invoice.tax_amount || 0).toFixed(2)}</td></tr>
              <tr data-testid="render-total"><td className="font-extrabold text-white py-2 px-3" style={{ background: NAVY }}>INVOICE TOTAL</td><td className="text-right py-2 px-3 font-extrabold text-white tabular-nums" style={{ background: NAVY }}>{usd(totalDue)}</td></tr>
              <tr><td className="font-bold py-1.5 px-3 border-t border-slate-300" style={{ color: '#15803d' }}>PAID TO DATE</td><td className="text-right py-1.5 px-3 border-t border-slate-300 tabular-nums" style={{ color: '#15803d' }} data-testid="render-paid">− {usd(invoice.paid_amount || 0)}</td></tr>
              <tr data-testid="render-balance-due">
                <td className="font-extrabold py-2 px-3 border-t-2" style={{ borderColor: NAVY, color: balanceDue > 0 ? ORANGE : '#15803d' }}>BALANCE DUE</td>
                <td className="text-right py-2 px-3 font-extrabold border-t-2 tabular-nums" style={{ borderColor: NAVY, color: balanceDue > 0 ? ORANGE : '#15803d' }}>{usd(balanceDue)}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

      {/* ===== Deposit + Payments History ===== */}
      {((Number(invoice.deposit_paid) || 0) > 0 || (Number(invoice.deposit_required) || 0) > 0 || (invoice.payments || []).length > 0) && (
        <section className="border-2 rounded-sm p-3 mb-4" style={{ borderColor: NAVY }}>
          <h3 className="font-extrabold text-sm tracking-wider mb-2" style={{ color: NAVY }}>DEPOSIT &amp; PAYMENT HISTORY</h3>
          {((Number(invoice.deposit_paid) || 0) > 0 || (Number(invoice.deposit_required) || 0) > 0) && (
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-2">
              <div className="flex items-baseline gap-2">
                <span className="uppercase font-bold text-[10px] tracking-wider" style={{ color: NAVY }}>DEPOSIT REQUIRED:</span>
                <span className="border-b border-slate-700 flex-1 px-1 tabular-nums">$ {(invoice.deposit_required || 0).toFixed(2)}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="uppercase font-bold text-[10px] tracking-wider" style={{ color: NAVY }}>DEPOSIT PAID:</span>
                <span className="border-b border-slate-700 flex-1 px-1 tabular-nums" data-testid="render-deposit-paid">$ {(invoice.deposit_paid || 0).toFixed(2)}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="uppercase font-bold text-[10px] tracking-wider" style={{ color: NAVY }}>DATE:</span>
                <span className="border-b border-slate-700 flex-1 px-1">{fmtDate(invoice.deposit_paid_date)}</span>
              </div>
            </div>
          )}
          {(invoice.payments || []).length > 0 && (
            <table className="w-full text-[12px] mt-2">
              <thead style={{ color: NAVY }}>
                <tr className="border-b" style={{ borderColor: NAVY }}>
                  <th className="text-left py-1 font-bold tracking-wider">DATE</th>
                  <th className="text-left py-1 font-bold tracking-wider">METHOD</th>
                  <th className="text-left py-1 font-bold tracking-wider">NOTE</th>
                  <th className="text-right py-1 font-bold tracking-wider">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.payments || []).map((p) => (
                  <tr key={p.id} className="border-b border-slate-300">
                    <td className="py-1">{fmtDate(p.date)}</td>
                    <td className="py-1">{p.method}</td>
                    <td className="py-1 text-slate-600">{p.note || '\u00A0'}</td>
                    <td className="py-1 text-right font-semibold tabular-nums" style={{ color: '#15803d' }}>{usd(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* ===== Payment Instructions ===== */}
      <section className="border-2 rounded-sm p-3 mb-4 text-[11px] leading-relaxed" style={{ borderColor: NAVY, color: NAVY }}>
        <h3 className="font-extrabold text-sm tracking-wider mb-2">PAYMENT &amp; PICKUP</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Balance is due upon pickup unless otherwise arranged.</li>
          <li>Accepted methods: cash, check (payable to <em>TayLord Arms LLC</em>), or card.</li>
          <li>Firearm(s) not picked up within 30 days of completion may be subject to storage fees.</li>
          <li>Please bring a valid photo ID for firearm pickup.</li>
          <li>By signing below, you acknowledge receipt of the firearm(s) and services described above.</li>
        </ul>
        <div className="grid grid-cols-12 gap-3 mt-4 items-end">
          <div className="col-span-6 flex items-end gap-1">
            <span className="font-bold">X</span>
            <div className="flex-1 border-b border-slate-700 h-6" />
          </div>
          <div className="col-span-4 border-b border-slate-700 h-6" />
          <div className="col-span-2 border-b border-slate-700 h-6" />
          <div className="col-span-6 text-[10px] font-bold tracking-wider">CUSTOMER SIGNATURE</div>
          <div className="col-span-4 text-[10px] font-bold tracking-wider">TAYLORD ARMS REPRESENTATIVE</div>
          <div className="col-span-2 text-[10px] font-bold tracking-wider">DATE</div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="text-center mt-4 pt-2 border-t-2" style={{ borderColor: NAVY }}>
        <p className="font-extrabold tracking-wider text-sm" style={{ color: NAVY }}>THANK YOU FOR YOUR BUSINESS — TAYLORD ARMS LLC.</p>
        <p className="font-bold italic text-xs mt-1" style={{ color: ORANGE }}>KEEP YOUR FIREARM RUNNING RIGHT.</p>
      </footer>
    </div>
  );
};

export default StructuredInvoiceView;
